import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError, formatEuro } from '../utils';
import Card from 'react-bootstrap/Card';
import { Container } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';
import { Form } from 'react-bootstrap';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        products: action.payload.products,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    default:
      return state;
  }
};

export default function ProductListScreen() {
  const [{ loading, error, products, pages, loadingCreate }, dispatch] =
    useReducer(reducer, {
      loading: true,
      error: '',
    });

  const navigate = useNavigate();
  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present

  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(
          `/api/products/admin?page=${page}&search=${encodeURIComponent(
            searchQuery
          )}`
        );

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    }; // fetches the data of all products and information that is being used for the pagination from the backend

    fetchData();
  }, [page, searchQuery]);

  const createHandler = async () => {
    if (window.confirm('Willst du dieses Produkt wirklich erstellen?')) {
      // if the user confirms that he wants to create a new product, the following code will run
      try {
        dispatch({ type: 'CREATE_REQUEST' });
        const { data } = await api.post('/api/products', {}); // posts a new product to the '/api/products' backend api
        toast.success('Produkt erstellt');
        dispatch({ type: 'CREATE_SUCCESS' });
        navigate(`/admin/product/${data.product._id}`); // navigates the user to the newly created product
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'CREATE_FAIL',
        });
      }
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.replace(/\s+/g, ''); // Remove spaces
    navigate(
      sanitizedInput
        ? `/admin/products?page=1&search=${encodeURIComponent(sanitizedInput)}`
        : `/admin/products?page=1`
    ); // Navigates to the ProductListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  return (
    <Container>
      <div>
        <title>Produkte</title>

        <h1 className="screen-heading">Produkte</h1>

        {/* Search Form */}
        <Form onSubmit={submitHandler} className="d-flex my-5">
          <Form.Control
            type="text"
            placeholder="Search by name, slug, _id or layout _id"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search by name, slug, _id or layout _id"
          />
          <Button type="submit" variant="secondary" className="mx-3">
            Search
          </Button>
        </Form>

        {loading || loadingCreate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row>
              <Col>
                <div className="d-flex justify-content-center mb-5">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={createHandler}
                  >
                    Produkt erstellen
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="d-xl-none">
              {products.map((product) => (
                <Col key={product._id} xs={12} sm={12} md={12} lg={6}>
                  <Card className="products-card">
                    <Card.Body className="text-center">
                      <Card.Title className="code mb-4">
                        {product.name}
                      </Card.Title>
                      <div className="divider"></div>
                      <div className="code-info">
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Kategorie:
                          </strong>{' '}
                          <span className="no-wrap">{product.category}</span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Preis:
                          </strong>{' '}
                          <span className="no-wrap">
                            {formatEuro(product.unitGross)}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Aktionen:
                          </strong>
                          <span className="no-wrap w-100 mt-1">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                navigate(`/admin/product/${product._id}`)
                              }
                            >
                              Bearbeiten
                            </Button>
                            &nbsp;
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() => {
                                toast.info(
                                  'This button is disabled, as Product documents can be referenced inside Order documents. In order to be able to trace which product was involved in an order afterwards, products cannot be completely deleted. In additon, deleting a product would also destruct other logic in the web app. Instead you have the opportunity to set the isActive state of this Product to false, which removes it from the Customizer and all existing Designs that rely on the Layout referenced in this Product will no longer show up in the CommunityDesginsScreen and MyDesignsScreen (so they are no longer available for purchase).'
                                );
                              }}
                            >
                              Löschen
                            </Button>
                          </span>
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-none d-xl-block">
              <table
                className="table"
                style={{ tableLayout: 'fixed', width: '100%' }}
              >
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>NAME</th>
                    <th>KATEGORIE</th>
                    <th>PREIS</th>
                    <th>AKTIONEN</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product._id}>
                      <td
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {product.name}
                      </td>
                      <td
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {product.category}
                      </td>
                      <td>{formatEuro(product.unitGross)}</td>
                      <td>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() =>
                            navigate(`/admin/product/${product._id}`)
                          }
                        >
                          Bearbeiten
                        </Button>
                        &nbsp;
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => {
                            toast.info(
                              'This button is disabled, as Product documents can be referenced inside Order documents. In order to be able to trace which product was involved in an order afterwards, products cannot be completely deleted. In additon, deleting a product would also destruct other logic in the web app. Instead you have the opportunity to set the isActive state of this Product to false, which removes it from the Customizer and all existing Designs that rely on the Layout referenced in this Product will no longer show up in the CommunityDesginsScreen and MyDesignsScreen (so they are no longer available for purchase).'
                            );
                          }}
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/products"
              search={searchQuery}
            />
          </>
        )}
      </div>
    </Container>
  );
}
